import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { storeLang, stripLangPrefix } from '../../i18n/i18n';
import { translatePath, englishToGermanPath } from '../../i18n/slugMap';
import { useAuth } from '../../context/AuthContext';
import { usersService } from '../../services';
import Icon from './Icon';
import styles from '../../styles/components/LanguageSwitcher.module.css';

/**
 * LanguageSwitcher — Umschalter Deutsch ↔ English.
 *
 * Ein Sprachwechsel ist bewusst eine ECHTE Navigation (Hard-Link statt
 * State-Wechsel): die Sprache lebt im URL-Präfix und der Router läuft mit
 * basename — nur ein voller Reload wechselt den Router-Kontext sauber.
 * Die Wahl wird gespeichert und gewinnt künftig vor der Browser-Sprache.
 *
 * Props:
 * - variant: 'inline' (Textlinks, z.B. Footer) | 'select' (z.B. Settings)
 */
function LanguageSwitcher({ variant = 'inline' }) {
  const { i18n, t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const lang = i18n.language || 'de';

  // location.pathname ist durch basename bereits präfixfrei; bei englischen
  // Sonder-Slugs (/help) erst auf den deutschen Pfad normalisieren.
  const routePath = stripLangPrefix(location.pathname);
  const neutralPath = lang === 'en' ? englishToGermanPath(routePath) : routePath;

  const targetFor = (targetLang) =>
    translatePath(neutralPath, targetLang) + location.search;

  const handleSwitch = async (targetLang) => {
    storeLang(targetLang);
    // Eingeloggte User: Sprachwahl auch im Profil persistieren, damit sie
    // geräteübergreifend und für Backend-Mails (siehe email.service) gilt.
    // Best-effort vor der Hard-Navigation — localStorage greift ohnehin sofort.
    if (user) {
      try {
        await usersService.updateProfile({ locale: targetLang });
      } catch {
        // Persistenz fehlgeschlagen (offline o.ä.) — localStorage reicht als Fallback
      }
    }
    // Hard-Navigation: Router-basename und i18n werden beim Laden neu gesetzt
    window.location.assign(targetFor(targetLang));
  };

  if (variant === 'select') {
    return (
      <label className={styles.selectWrapper}>
        <span className={styles.selectLabel}>{t('languageSwitcher.label')}</span>
        <select
          className={styles.select}
          value={lang}
          onChange={(e) => handleSwitch(e.target.value)}
          aria-label={t('languageSwitcher.label')}
        >
          <option value="de">{t('languageSwitcher.de')}</option>
          <option value="en">{t('languageSwitcher.en')}</option>
        </select>
      </label>
    );
  }

  return (
    <div className={styles.inline} aria-label={t('languageSwitcher.label')}>
      <Icon name="globe" size={14} />
      <button
        type="button"
        className={`${styles.langButton} ${lang === 'de' ? styles.active : ''}`}
        onClick={() => lang !== 'de' && handleSwitch('de')}
        aria-current={lang === 'de' ? 'true' : undefined}
      >
        DE
      </button>
      <span className={styles.divider}>·</span>
      <button
        type="button"
        className={`${styles.langButton} ${lang === 'en' ? styles.active : ''}`}
        onClick={() => lang !== 'en' && handleSwitch('en')}
        aria-current={lang === 'en' ? 'true' : undefined}
      >
        EN
      </button>
    </div>
  );
}

export default LanguageSwitcher;
